import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { debounce } from '@amzn/storm-ui-utils';
import {
  SIDEBAR_TYPE,
  SideNavLinkItemType,
} from '@amzn/aac-chrome-internal-api';
import { useConsolidatedNavContext } from 'src/providers/ConsolidatedNavContextProvider';
import {
  Overlay,
  ToolbarWrapper,
} from 'src/features/nav/components/ConsolidatedSideNav/styles';
import SideNavLink from 'src/features/nav/components/ConsolidatedSideNav/SideNavLink';
import Accordion from 'src/features/nav/components/ConsolidatedSideNav/Accordion/Accordion';
import SideNavModal from './SideNavModal';
import SideNavButton from './SideNavButton';
import {
  CC_MODAL_BULLSEYE_TOOLBAR_ID,
  CC_MODAL_TOOLBAR_ID,
} from 'src/commons/constants';

const OverlayContainer = () => {
  const portalRef = document.getElementById('AACChromePortal') as Element;

  if (!portalRef) {
    return null;
  }

  return createPortal(
    <Overlay data-ccx-e2e-id="chrome-overlay" data-test-id="chrome-overlay" />,
    portalRef,
  );
};

export const ConsolidatedSideNavInner: React.VFC = () => {
  const { isQuickNavModalEnabled, isNavOpen, setIsNavOpen, toolbarItems } =
    useConsolidatedNavContext();

  const [isFocused, setIsFocused] = useState<boolean>(false);
  // This ref is required so that we can access the current isFocused value inside the debounce function below
  const isFocusedReference = useRef<boolean>();
  isFocusedReference.current = isFocused;

  return (
    <>
      <ToolbarWrapper
        onMouseEnter={() => !isNavOpen && setIsNavOpen(true)}
        onMouseLeave={() => isNavOpen && setIsNavOpen(false)}
        onFocus={() => {
          setIsFocused(true);
          !isNavOpen && setIsNavOpen(true);
        }}
        onBlur={() => {
          setIsFocused(false);
          // This is required because shifting the focus from one nav element to another triggers onBlur and then immediately triggers onFocus handler. We want to await for 100 ms to make sure other nav elements are not focused before collapsing the nav
          debounce(
            () =>
              !isFocusedReference.current && isNavOpen && setIsNavOpen(false),
            100,
          )();
        }}
        isOpen={isNavOpen}
        data-ccx-e2e-id="chrome-side-nav"
      >
        {toolbarItems
          .filter(
            (toolbarItem) =>
              toolbarItem.sidebarType !== SIDEBAR_TYPE.DIVIDER &&
              (toolbarItem?.id !== CC_MODAL_TOOLBAR_ID ||
                isQuickNavModalEnabled) &&
              toolbarItem.id !== CC_MODAL_BULLSEYE_TOOLBAR_ID,
          )
          .map((toolbarItem) => {
            const isSingleApp =
              toolbarItem.sidebarGroups.length == 1 &&
              toolbarItem.sidebarGroups[0].links.length == 1;

            if (isSingleApp) {
              if (
                toolbarItem.sidebarGroups[0].links[0].sidebarItemType ===
                ('MODAL' as SideNavLinkItemType)
              ) {
                return (
                  <SideNavButton key={toolbarItem.id} id={toolbarItem.id} />
                );
              }
              return (
                <SideNavLink
                  key={toolbarItem.id}
                  id={toolbarItem.id}
                  isQuickNavModalEnabled={isQuickNavModalEnabled}
                />
              );
            }

            return <Accordion key={toolbarItem.id} id={toolbarItem.id} />;
          })}
      </ToolbarWrapper>
      {isNavOpen && <OverlayContainer />}
      <SideNavModal />
    </>
  );
};
